import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import Header from "./components/Header";
import ChangePasswordPopup from "./components/ChangePasswordPopup";
import PushNotification from "../PushNotification/PushNotification";
import ClientsContextProvider from "../../context/clients/ClientsContextProvider";
import { CustomTimingContextProvider } from "../../context/CustomTimingContextProvider";
import DaySummary from "../../pages/operations/components/Modals/DaySummary";
import QUERY_KEYS from "../../constants/queryKeys";
import { handleGetDaySummaryStatusService } from "../../services/operations/summary";

interface LayoutProps {
  children: React.ReactNode;
}

function Layout({ children }: LayoutProps) {
  const [openDaySummaryDialog, setOpenDaySummaryDialog] = useState(false);
  const isAuthenticated = !!localStorage.getItem("userToken");

  const { data: summaryStatusData, refetch: refetchSummaryStatus } = useQuery({
    queryKey: [QUERY_KEYS.GET_DAY_SUMMARY_STATUS],
    queryFn: handleGetDaySummaryStatusService,
    enabled: isAuthenticated,
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    retry: 0,
    meta: { toastOnError: false, showGlobalLoader: false },
  });

  useEffect(() => {
    if (summaryStatusData?.data?.isDue) {
      setOpenDaySummaryDialog(true);
    }
  }, [summaryStatusData?.data?.isDue]);

  useEffect(() => {
    const handleOpenDaySummary = () => setOpenDaySummaryDialog(true);

    window.addEventListener("open-day-summary", handleOpenDaySummary);
    return () => {
      window.removeEventListener("open-day-summary", handleOpenDaySummary);
    };
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && isAuthenticated) {
        refetchSummaryStatus();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isAuthenticated, refetchSummaryStatus]);

  return (
    <ClientsContextProvider>
      <CustomTimingContextProvider>
        <div className="flex flex-col h-screen">
          <Header />

          {/* Main Content */}
          <div className="flex-1 flex flex-col overflow-hidden">{children}</div>
          <ChangePasswordPopup />
          <PushNotification />
          <DaySummary
            showModal={openDaySummaryDialog}
            onClose={() => setOpenDaySummaryDialog(false)}
          />
        </div>
      </CustomTimingContextProvider>
    </ClientsContextProvider>
  );
}

export default Layout;
